import { getSessionTabId } from './tabId';

/**
 * Utility functions for managing multi-tab state
 * Uses raw localStorage for cross-tab communication
 */

// Constants
const HEARTBEAT_TIMEOUT_MS = 3000; // 3 seconds
const RADIX_DECIMAL = 10;

// Storage keys
const STORAGE_KEYS = {
   ACTIVE_TAB: 'activeTabId',
   HEARTBEAT: 'tab_heartbeat',
   TIMESTAMP: 'tab_timestamp',
};

/**
 * Get the current active tab ID from localStorage
 * @returns {string|null} The active tab ID or null if none
 */
export const getActiveTabId = () => {
   return localStorage.getItem(STORAGE_KEYS.ACTIVE_TAB);
};

/**
 * Check if the heartbeat is stale
 * @returns {boolean} True if heartbeat is older than timeout
 */
export const isHeartbeatStale = () => {
   const value = localStorage.getItem(STORAGE_KEYS.HEARTBEAT);
   const lastHeartbeat = value ? parseInt(value, RADIX_DECIMAL) : 0;

   if (!lastHeartbeat) return true;

   const timeSinceLastHeartbeat = Date.now() - lastHeartbeat;
   return timeSinceLastHeartbeat > HEARTBEAT_TIMEOUT_MS;
};

/**
 * Check if the current session tab is active
 * @returns {boolean} True if this tab is active
 */
export const getIsThisTabActive = () => {
   const activeTabId = getActiveTabId();
   const tabId = getSessionTabId();
   if (!activeTabId || activeTabId === 'null') return true;
   return activeTabId === tabId;
};

/**
 * Set the active tab ID and initialize heartbeat
 * Stores timestamp to track newest tab
 * @param {string} tabId - The tab ID to set as active
 */
export const setActiveTab = tabId => {
   const timestamp = Date.now().toString();

   localStorage.setItem(STORAGE_KEYS.ACTIVE_TAB, tabId);
   localStorage.setItem(STORAGE_KEYS.HEARTBEAT, timestamp);
   localStorage.setItem(STORAGE_KEYS.TIMESTAMP, timestamp);
};

/**
 * Update the heartbeat for the current active tab
 */
export const updateHeartbeat = () => {
   const timestamp = Date.now().toString();
   localStorage.setItem(STORAGE_KEYS.HEARTBEAT, timestamp);
};

/**
 * Clear the active tab state
 */
export const clearActiveTab = () => {
   localStorage.removeItem(STORAGE_KEYS.ACTIVE_TAB);
   localStorage.removeItem(STORAGE_KEYS.HEARTBEAT);
   localStorage.removeItem(STORAGE_KEYS.TIMESTAMP);
};

/**
 * Check if this tab should automatically become active
 * New tabs (created after current active tab) should take priority
 * @param {number} thisTabTimestamp - Creation timestamp of this tab
 * @returns {boolean} True if this tab should become active
 */
export const shouldTakePriority = thisTabTimestamp => {
   const activeTabId = getActiveTabId();
   const isStale = isHeartbeatStale();

   // If no active tab or heartbeat is stale, take control
   if (!activeTabId || isStale) return true;

   // If this tab is newer than the active tab, take control
   const value = localStorage.getItem(STORAGE_KEYS.TIMESTAMP);
   const activeTabTimestamp = value ? parseInt(value, RADIX_DECIMAL) : 0;

   return thisTabTimestamp > activeTabTimestamp;
};
